import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { DialogModule } from 'primeng/dialog';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { AgentQueueDialogService } from 'src/core/services/agent-queue/agent-queue-dialog.service';
import { QueueDto } from 'src/app/administration/settings/queues/models/queue.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { CxMultiSelectComponent } from 'src/app/shared/components/cx-multi-select/cx-multi-select.component';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { AgentManagementListItemDto } from '../../models/agent-management-list-item.dto';

@Component({
  selector: 'app-assign-queue-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, LocalizationPipe, DialogModule, CxMultiSelectComponent, FormActionsComponent],
  templateUrl: './assign-queue-dialog.component.html',
  styleUrl: './assign-queue-dialog.component.scss',
})
export class AssignQueueDialogComponent implements OnInit, OnDestroy {
  private readonly dialogService = inject(AgentQueueDialogService);
  private readonly crudService = inject(CrudService);
  private readonly toasterService = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  visible = false;
  agent: AgentManagementListItemDto | null = null;
  allQueues: QueueDto[] = [];
  selectedQueueIds: number[] = [];
  loading = false;
  saving = false;
  private panelWidthObserver: MutationObserver | null = null;

  get queuesSelectedLabel(): string {
    return `{0} ${this.localization.instant('AgentManagement::AssignQueue:QueuesSelected')}`;
  }

  ngOnInit(): void {
    this.dialogService.open$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(agent => {
        this.agent = agent;
        this.selectedQueueIds = [];
        this.visible = true;
        this.cdr.markForCheck();
        this.loadQueues();
        this.setupPanelWidthSync();
      });
  }

  ngOnDestroy(): void {
    if (this.panelWidthObserver) {
      this.panelWidthObserver.disconnect();
    }
  }

  private setupPanelWidthSync(): void {
    this.ngZone.runOutsideAngular(() => {
      this.panelWidthObserver = new MutationObserver(() => {
        const panel = document.querySelector('.cx-multi-select-panel');
        const input = document.querySelector('.p-multiselect.cx-multi-select__control');

        if (panel && input && (panel as HTMLElement).offsetParent !== null) {
          const inputWidth = input.getBoundingClientRect().width;
          const panelEl = panel as HTMLElement;
          panelEl.style.width = inputWidth + 'px';
        }
      });

      this.panelWidthObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  onCancel(): void {
    this.visible = false;
  }

  onHide(): void {
    this.visible = false;
  }

  onSave(): void {
    if (!this.agent || this.saving) {
      return;
    }

    const agentId = this.agent.id;
    const originalQueueIds = new Set((this.agent.queues ?? []).map(q => q.id));
    const newQueueIds = new Set(this.selectedQueueIds);

    const loadedQueueIds = new Set(
      this.allQueues.map(q => q.id).filter((id): id is number => id !== null),
    );

    const addedIds = new Set([...newQueueIds].filter(id => !originalQueueIds.has(id)));
    const removedIds = new Set(
      [...originalQueueIds].filter(id => !newQueueIds.has(id) && loadedQueueIds.has(id)),
    );
    const affectedIds = new Set([...addedIds, ...removedIds]);

    if (affectedIds.size === 0) {
      this.visible = false;
      return;
    }

    const removesLastAgent = this.allQueues.some(
      q => q.id !== null && removedIds.has(q.id) && (q.assignedUsers ?? []).length === 1,
    );

    if (removesLastAgent) {
      this.confirmationService
        .warn('Settings::Queues:LastAgentMessage', 'Settings::Queues:LastAgentTitle')
        .subscribe(status => {
          if (status === Confirmation.Status.confirm) {
            this.performSave(agentId, addedIds, removedIds, affectedIds);
          }
        });
      return;
    }

    this.performSave(agentId, addedIds, removedIds, affectedIds);
  }

  private performSave(
    agentId: string,
    addedIds: Set<number>,
    removedIds: Set<number>,
    affectedIds: Set<number>,
  ): void {
    this.saving = true;

    const updates = this.allQueues
      .filter(q => q.id !== null && affectedIds.has(q.id))
      .map(queue => {
        const queueId = queue.id!;
        let users = [...(queue.assignedUsers ?? [])];

        if (addedIds.has(queueId)) {
          if (!users.some(u => u.userId === agentId)) {
            users = [...users, { queueId, userId: agentId }];
          }
        } else {
          users = users.filter(u => u.userId !== agentId);
        }

        const payload = {
          ...queue,
          id: queueId,
          assignedUsers: users.map(u => ({
            queueId: u.queueId ?? queueId,
            agentId: u.userId,
            userId: u.userId,
          })),
        };

        return this.crudService.update<QueueDto>(QueueDto, payload as any);
      });

    let settled = false;

    forkJoin(updates).subscribe({
      next: () => {
        settled = true;
        this.saving = false;
        this.visible = false;
        this.toasterService.success(
          'AgentManagement::AssignQueue:SaveSuccess',
          'AgentManagement::AssignQueue:Title',
        );
        this.dialogService.notifyQueuesSaved();
        this.cdr.markForCheck();
      },
      error: () => {
        settled = true;
        this.saving = false;
        this.cdr.markForCheck();
      },
      complete: () => {
        if (!settled) {
          this.saving = false;
          this.dialogService.notifyQueuesSaved();
          this.cdr.markForCheck();
        }
      },
    });
  }

  private loadQueues(): void {
    this.loading = true;

    this.crudService
      .get<QueueDto>(QueueDto, { filters: [], sorters: [], skipCount: 0, maxResultCount: 500 })
      .subscribe({
        next: (res: ListResponseDto<QueueDto>) => {
          this.allQueues = (res.items ?? [])
            .filter(item => item.isActive)
            .map(item => Object.assign(new QueueDto(), item));
          const agentQueueIds = new Set((this.agent?.queues ?? []).map(q => q.id));
          this.selectedQueueIds = this.allQueues
            .filter(q => q.id !== null && agentQueueIds.has(q.id))
            .map(q => q.id as number);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
